import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { generateTable } from "@/config/dashboard/general";
import {
  publicAuctionColumns,
  publicAuctionColumnsVisibility,
} from "@/config/dashboard/public-auction";
import { Auction } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { AuctionLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * AuctionLinks component
 * @returns {JSX.Element}
 */
const AuctionLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedAuctions, setPaginationRelatedAuctions] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedAuction",
    getPaginationRelatedAuctions.pageSize,
    getPaginationRelatedAuctions.page * getPaginationRelatedAuctions.pageSize,
  );

  const { data: countRelatedAuctions } = useCountRelatedFields(id, type, [
    "relatedAuction",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedAuction",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedAuction",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedAuctionId, setSelectedAuctionId] = useState<string | null>(null);
  const [searchContent, setSearchContent] = useState("");

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const handleCreateRelation = async (idToLink: string) => {
    try {
      await createRelatedFields([id, idToLink]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRelation = async (photographyId: string) => {
    await deleteRelation(photographyId);
  };

  const baseColumns = useMemo(
    () => generateTable(publicAuctionColumns, "auction", t),
    [t],
  );

  const renderAuctionRowActions = useCallback(
    (auction: Auction) => (
      <RelatedRecordRowActions
        isAddMode={isAddMode}
        resourceId={auction.id}
        previewType="auction"
        previewElements={[
          {
            label: t("ventesDescriptionNumeroVente"),
            value: auction.ventesDescriptionNumeroVente,
            type: "text",
          },
          {
            label: t("ventesDescriptionResume"),
            value: auction.ventesDescriptionResume,
            type: "richtext",
          },
          {
            label: t("attachments"),
            value: auction.attachments ?? [],
            type: "image",
          },
        ]}
        onDelete={() => handleDeleteRelation(auction.id)}
        onAdd={() => handleCreateRelation(auction.id)}
      />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Auction>(({ row }) =>
        renderAuctionRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderAuctionRowActions]);

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const STORAGE_KEY = "auction-links-table";
  const defaultVisibility = { ...publicAuctionColumnsVisibility, actions: true };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedAuctions,
    setPage: setPaginationRelatedAuctions,
    totalSize: countRelatedAuctions?.relatedAuction ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedPublicAuctions")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notAlreadyRelatedAuctions?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Auction) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderAuctionRowActions(row)}>
                <AuctionLinkGridCard
                  item={row}
                  t={t}
                  onTextClick={() => setSelectedAuctionId(row.id)}
                />
              </GridCardWithActions>
              <ShowElements
                type="auction"
                id={row.id}
                open={selectedAuctionId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedAuctionId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("ventesDescriptionNumeroVente"),
                    value: row.ventesDescriptionNumeroVente,
                    type: "text",
                  },
                  {
                    label: t("ventesDescriptionResume"),
                    value: row.ventesDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments ?? [],
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default AuctionLinks;
